// Traduccion de los nombres de las categorias de eventos.
//
// El catalogo (`GET /api/obtener/categorias`) devuelve el nombre literal en
// espanol ("Cultura"), no una clave del esquema de textos. Por eso el
// `resolveLabel` generico del selector — `texts?.[rawLabel] || rawLabel` — no
// traducia nada: buscaba una clave llamada "Cultura", que no existe.
//
// Del esquema remoto solo 6 de las 13 categorias tienen clave. Verificado contra
// /api/lang/sch el 2026-08-19. Las 7 restantes se traducen aqui hasta que backend
// las de de alta y el catalogo devuelva la clave de cada categoria; ese dia basta
// con extender EVENT_CATEGORY_TEXT_KEYS y retirar el respaldo local.

// Sin acentos y en minusculas, para que el catalogo pueda escribir "Gastronomia"
// o "GASTRONOMÍA" y siga resolviendo.
const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// Categorias que SI viven en el esquema remoto.
export const EVENT_CATEGORY_TEXT_KEYS = {
  cultura: "LB0056",
  musica: "LB0274",
  deporte: "LB0275",
  viajes: "LB0273",
  tecnologia: "LB0278",
  otro: "LB0900",
};

// Respaldo local. Cubre las 13, no solo las 7 sin clave: el esquema se carga de
// forma asincrona, asi que si todavia no llego (o falla) las 6 que dependen de el
// se verian en espanol dentro de la interfaz en ingles. El esquema sigue teniendo
// prioridad cuando esta disponible, asi que esto no compite con el, lo cubre.
export const EVENT_CATEGORY_FALLBACK = {
  cultura: { es: "Cultura", en: "Culture" },
  musica: { es: "Música", en: "Music" },
  deporte: { es: "Deporte", en: "Sports" },
  viajes: { es: "Viajes", en: "Travel" },
  tecnologia: { es: "Tecnología", en: "Technology" },
  otro: { es: "Otro", en: "Other" },
  social: { es: "Social", en: "Social" },
  gastronomia: { es: "Gastronomía", en: "Food & Drink" },
  aventura: { es: "Aventura", en: "Adventure" },
  bienestar: { es: "Bienestar", en: "Wellness" },
  educacion: { es: "Educación", en: "Education" },
  networking: { es: "Networking", en: "Networking" },
  voluntariado: { es: "Voluntariado", en: "Volunteering" },
};

export const resolveEventCategoryLabel = (
  rawLabel,
  { texts, isEnglish } = {}
) => {
  const key = normalize(rawLabel);
  if (!key) return rawLabel ?? "";

  // 1. El esquema manda cuando la categoria tiene clave.
  const schemeKey = EVENT_CATEGORY_TEXT_KEYS[key];
  const fromScheme = schemeKey ? texts?.[schemeKey] : null;
  if (fromScheme) return fromScheme;

  // 2. Respaldo local para las que no la tienen.
  const fallback = EVENT_CATEGORY_FALLBACK[key];
  if (fallback) return isEnglish ? fallback.en : fallback.es;

  // 3. Categoria desconocida: se muestra tal como llega, nunca vacio.
  return rawLabel;
};

// ---------------------------------------------------------------------------
// VIVA-1027: la categoria del evento en el detalle.
//
// Comprobado contra el ambiente el 11/09/2026 con el evento "Dinamica de
// integracion anual GRUPO CSI": `POST /api/detalles/eventos` SI devuelve la
// categoria, pero no como objeto ni con su nombre. La manda en dos campos planos
// del evento:
//
//   idCategoriaEvento: 1
//   nombreCategoriaEvento: "LB1160"   <- pese al nombre, es el CODIGO, no el nombre
//
// Y el esquema remoto de textos NO publica todavia esos codigos (verificado: de
// las 1388 llaves cargadas no existe ninguna LB1160-LB1172), asi que resolverlos
// contra `texts` devuelve vacio. De ahi que el detalle no mostrara nada.
//
// El unico lugar donde vive el nombre es el catalogo `/api/obtener/categorias`,
// que ya se consume en el listado y en el formulario:
//   { idCategoriaEvento: 1, codigo: "LB1160", nombre: "Social", orden, activo }
// Por eso la pantalla pasa ese catalogo en `catalog` y aqui se cruza por codigo o
// por id. El dia que backend de de alta los codigos en el esquema, `texts` gana
// solo y esta funcion no cambia.
//
// TODA la lectura del campo vive aqui a proposito: si backend renombra algo,
// corregirlo es tocar este archivo y nada mas.

// Contenedores posibles de la categoria dentro del evento. Se leen de forma
// defensiva igual que `getFirstValue("modalidadEvento", "ModalidadEvento",
// "modalidad")` en CardDetails.jsx: el mismo dato llega en camelCase o PascalCase
// segun el endpoint.
const CATEGORY_FIELDS = [
  "categoria",
  "Categoria",
  "categoriaEvento",
  "CategoriaEvento",
  "categorias",
  "Categorias",
];

// Campos planos del detalle, para cuando la categoria no viene anidada.
const FLAT_ID_FIELDS = ["idCategoriaEvento", "IdCategoriaEvento"];
const FLAT_CODE_FIELDS = [
  "nombreCategoriaEvento",
  "NombreCategoriaEvento",
  "codigoCategoriaEvento",
  "CodigoCategoriaEvento",
];

const NAME_FIELDS = ["nombre", "Nombre", "name", "descripcion", "Descripcion"];

// Las claves del esquema remoto tienen forma LB0056 / AL0123. Se detectan por su
// FORMA y no por el nombre del campo que las transporta, porque ese nombre no esta
// confirmado y adivinarlo es justo el error que este ticket documenta.
const SCHEME_KEY_PATTERN = /^[A-Z]{2}\d{3,}$/;

const isSchemeKey = (value) => SCHEME_KEY_PATTERN.test(String(value ?? "").trim());

const firstDefined = (source, fields) => {
  for (const field of fields) {
    const value = source?.[field];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

// De una categoria cruda saca { id, codigo, nombre }. Devuelve null si no hay
// nada aprovechable, para que el llamador simplemente omita el campo.
const readCategory = (raw) => {
  if (!raw) return null;

  // Puede llegar como cadena suelta: o es la clave del esquema, o ya es el nombre.
  if (typeof raw === "string") {
    const value = raw.trim();
    if (!value) return null;
    return isSchemeKey(value)
      ? { id: "", codigo: value, nombre: "" }
      : { id: "", codigo: "", nombre: value };
  }

  if (typeof raw !== "object") return null;

  const rawName = firstDefined(raw, NAME_FIELDS);
  const nombre = rawName === null ? "" : String(rawName).trim();

  // El codigo se busca por forma entre los valores del objeto de categoria. El
  // barrido se limita a ese objeto: hacerlo sobre el evento completo encontraria
  // `modalidadEvento` ("LB0744") o `estadoEvento`, que son de otra cosa.
  const codigoEncontrado = Object.values(raw).find(isSchemeKey);
  const codigo = codigoEncontrado ? String(codigoEncontrado).trim() : "";

  // Si el propio campo de nombre trae la clave, es la clave y no un nombre: no se
  // debe mostrar "LB1161" como si fuera el nombre de la categoria.
  const nombreLimpio = isSchemeKey(nombre) ? "" : nombre;

  const rawId = firstDefined(raw, FLAT_ID_FIELDS.concat(["id", "Id"]));
  const id = rawId === null ? "" : String(rawId).trim();

  if (!nombreLimpio && !codigo && !id) return null;
  return { id, codigo, nombre: nombreLimpio };
};

// Nombre de la categoria segun el catalogo `/api/obtener/categorias`. Cruza por
// codigo y, si no, por id: el detalle manda los dos y el catalogo trae ambos.
const findInCatalog = (catalog, { id, codigo }) => {
  if (!Array.isArray(catalog) || catalog.length === 0) return "";

  const match = catalog.find((item) => {
    const itemCode = String(item?.codigo ?? item?.Codigo ?? "").trim();
    const itemId = String(
      item?.idCategoriaEvento ?? item?.IdCategoriaEvento ?? item?.id ?? ""
    ).trim();

    if (codigo && itemCode) return itemCode === String(codigo);
    return Boolean(id) && itemId === String(id);
  });

  const nombre = match?.nombre ?? match?.Nombre ?? "";
  return String(nombre).trim();
};

/**
 * Etiquetas de categoria del evento, ya traducidas al idioma de la sesion.
 *
 * Devuelve SIEMPRE un arreglo: vacio cuando el evento no trae categoria, cuando
 * llega vacia o cuando no hay forma de nombrarla. Nunca devuelve "null",
 * "undefined" ni la clave cruda — de ahi sale el criterio de "sin categoria no se
 * pinta el campo".
 *
 * `catalog` es el catalogo de `/api/obtener/categorias` tal como lo devuelve
 * `getEventCategoriesService`. Es opcional: sin el se resuelve lo que se pueda,
 * y con el se traduce el codigo plano que manda el detalle.
 *
 * Acepta una categoria o varias; si el modelo pasa a admitir N por evento, el
 * render ya las soporta sin tocar nada.
 */
export const getEventCategoryLabels = (
  event,
  { texts, isEnglish, catalog = [] } = {}
) => {
  const source = event ?? {};
  const raw = firstDefined(source, CATEGORY_FIELDS);

  // Sin categoria anidada se arma una con los campos planos del detalle.
  const lista =
    raw === null
      ? [
          {
            id: firstDefined(source, FLAT_ID_FIELDS),
            codigo: firstDefined(source, FLAT_CODE_FIELDS),
          },
        ]
      : Array.isArray(raw)
      ? raw
      : [raw];

  return lista
    .map(readCategory)
    .filter(Boolean)
    .map(({ id, codigo, nombre }) => {
      // 1. La clave del esquema manda: ya llega en el idioma de la sesion.
      const desdeEsquema = codigo ? texts?.[codigo] : null;
      if (desdeEsquema) return String(desdeEsquema).trim();

      // 2. El esquema todavia no publica los codigos de categoria, asi que el
      //    nombre sale del catalogo cuando el evento no lo trae.
      const nombreResuelto = nombre || findInCatalog(catalog, { id, codigo });

      // 3. Ese nombre viene en espanol; se traduce con el resolutor de arriba
      //    (respaldo local {es,en} de las 13 categorias).
      return String(
        resolveEventCategoryLabel(nombreResuelto, { texts, isEnglish }) ?? ""
      ).trim();
    })
    .filter(Boolean);
};
